import React, { useState } from 'react';
import uuid from 'uuid';
import PropTypes from 'prop-types';
import AccordionEdit from './AccordionEdit';
import { defaultAccordion } from '../DefaultAccordions';

const propTypes = {
  onCancelEdit: PropTypes.func.isRequired,
  onFinishEdit: PropTypes.func.isRequired,
  content: PropTypes.string,
  code: PropTypes.string,
  script: PropTypes.string,
  border: PropTypes.bool,
  height: PropTypes.string,
  contentType: PropTypes.string,
  scrollbars: PropTypes.bool,
  accordions: PropTypes.arrayOf(PropTypes.object),
};

const defaultProps = {
  content: undefined,
  code: undefined,
  script: undefined,
  border: undefined,
  height: undefined,
  contentType: undefined,
  scrollbars: undefined,
  accordions: [],
};

const AccordionEditor = (props) => {
  const { accordions, onCancelEdit } = props;
  const [latestAccordions, setLatestAccordions] = useState(accordions);
  const [isDragging, setIsDragging] = useState(false);
  const [invalidTitles, setInvalidTitles] = useState([]);
  const [invalidBodies, setInvalidBodies] = useState([]);
  const [invalidAccordions, setInvalidAccordions] = useState([]);
  const [panelCloseStatesEditMode, setPanelCloseStatesEditMode] = useState({});

  const checkIfEmptyRawContent = rawContentState => rawContentState.blocks.every(block => block.text.trim() === '');


  const checkInValidAccordions = (updatedAccordions) => {
    const invalidAccordionTitles = [];
    const invalidAccordionBodies = [];

    updatedAccordions.forEach((accordion) => {
      if (checkIfEmptyRawContent(accordion.title)) {
        invalidAccordionTitles.push(accordion.id);
      }

      if (checkIfEmptyRawContent(accordion.body)) {
        invalidAccordionBodies.push(accordion.id);
      }
    });

    setInvalidTitles(invalidAccordionTitles);
    setInvalidBodies(invalidAccordionBodies);
    return [...new Set([...invalidAccordionTitles, ...invalidAccordionBodies])];
  };

  const togglePanelEditMode = (accordionId) => {
    setPanelCloseStatesEditMode(prevState => ({
      ...prevState,
      [accordionId]: !prevState[accordionId],
    }));
  };

  const draggingHandler = (value) => {
    setIsDragging(value);
  };

  const finishEdit = () => {
    const { onFinishEdit } = props;
    if (invalidAccordions.length > 0) {
      return;
    }
    onFinishEdit({
      initialEdit: false,
      accordions: latestAccordions,
      scrollbars: false,
      border: false,
    });
  };

  const addAccordion = () => {
    const newAccordion = {
      ...defaultAccordion,
      id: uuid.v4(),
    };
    const newAccordions = [...latestAccordions, newAccordion];
    setLatestAccordions(newAccordions);
  };

  const defaultOpenChangeHandler = (id, defaultOpen) => {
    const newAccordions = latestAccordions.map((accordion) => {
      if (accordion.id === id) {
        return {
          ...accordion,
          defaultOpen,
        };
      }
      return accordion;
    });
    setLatestAccordions(newAccordions);
  };

  const deleteAccordionHandler = (id) => {
    const newAccordions = latestAccordions.filter(accordion => accordion.id !== id);
    setInvalidAccordions(checkInValidAccordions(newAccordions));
    setLatestAccordions(newAccordions);
  };

  const reOrderAccordionsHandler = (reorderedAccordions) => {
    setLatestAccordions(reorderedAccordions);
  };


  const editAccordionHandler = (accordionItemId, contentType, content) => {
    const newAccordions = latestAccordions.map((accordion) => {
      if (accordion.id === accordionItemId) {
        return {
          ...accordion,
          [contentType]: content.rawContentState,
        };
      }
      return accordion;
    });
    setInvalidAccordions(checkInValidAccordions(newAccordions));
    setLatestAccordions(newAccordions);
  };

  return (
        <AccordionEdit
          {...props}
          accordions={latestAccordions}
          deleteAccordion={deleteAccordionHandler}
          editAccordion={editAccordionHandler}
          defaultOpenChange={defaultOpenChangeHandler}
          reOrderAccordionsHandler={reOrderAccordionsHandler}
          onFinishEdit={finishEdit}
          onCancelEdit={onCancelEdit}
          addAccordion={addAccordion}
          invalidTitles={invalidTitles}
          invalidBodies={invalidBodies}
          draggingHandler={draggingHandler}
          isDragging={isDragging}
          togglePanelEditMode={togglePanelEditMode}
          panelCloseStatesEditMode={panelCloseStatesEditMode}
        />
  );
};

AccordionEditor.defaultProps = defaultProps;
AccordionEditor.propTypes = propTypes;

export default AccordionEditor;
